/**
 * React Query hooks for Digital Twin data.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { Pruefstatus, EntityEvent } from '@hofsync/design-system';
import {
  twinService,
  type EventFilters,
  type EntityListResponse,
  type EventListResponse,
  type TwinStats,
  type LenseConfig,
  type LenseCounts,
  type LiquiditaetLinkListResponse,
  type LiquiditaetLinkResult,
} from '../services/twinService';

/**
 * Issue #593 (2026-05-02): default page size for the FarmPage entity list,
 * mirrors ``useDocuments``' ``DEFAULT_PAGE_SIZE`` so the two paginated
 * surfaces share one chrome density.
 */
export const DEFAULT_ENTITY_PAGE_SIZE = 14;

/** HOF-626 — OUTSIDE the ``['twin', …]`` namespace: the SSE ``twin:processing_completed``
 *  sweep invalidates ``['twin']`` and must not re-scan the tenant's Redis buffer. */
export const pendingEditsKey = (entityType: string, entityId: string) =>
  ['twin-pending-edits', entityType, entityId] as const;

/**
 * Query key factory for twin data.
 */
export const twinKeys = {
  all: ['twin'] as const,
  events: {
    all: ['twin', 'events'] as const,
    list: (filters?: EventFilters) => ['twin', 'events', 'list', filters] as const,
  },
  pruefstatus: {
    all: ['twin', 'pruefstatus'] as const,
    area: (id: string) => ['twin', 'pruefstatus', id] as const,
  },
  entities: {
    all: ['twin', 'entities'] as const,
    list: (
      entityType: string,
      filters?: Record<string, string>,
      page?: number,
      pageSize?: number,
      filterMissing?: string[],
      q?: string | null,
    ) => {
      // ``missingKey`` and ``searchKey`` are appended only when set so
      // the legacy ``[twin, entities, type, filters]`` 4-tuple still
      // hits the ``useFarmBootstrap`` pre-seed for callers without
      // either ``filter_missing`` or the issue-#637 search.
      const missingKey =
        filterMissing && filterMissing.length > 0 ? filterMissing.join(',') : undefined;
      const searchKey = q && q.trim().length > 0 ? q.trim() : undefined;
      // When the caller doesn't paginate (page/pageSize omitted) the key
      // matches the legacy 3-element shape so the ``useFarmBootstrap``
      // pre-seed (Phase 07-6 kill-list #1) keeps warming the cold-load.
      // Paginated callers append page+pageSize so each visible page caches
      // independently and a Paginator click never blocks on a re-fetch of
      // pages already seen in the same session.
      const tail: unknown[] = [];
      if (missingKey !== undefined) tail.push(missingKey);
      if (searchKey !== undefined) {
        if (tail.length === 0) tail.push(undefined);
        tail.push(searchKey);
      }
      if (page == null && pageSize == null) {
        return tail.length === 0
          ? (['twin', 'entities', entityType, filters] as const)
          : (['twin', 'entities', entityType, filters, ...tail] as const);
      }
      return tail.length === 0
        ? (['twin', 'entities', entityType, filters, page, pageSize] as const)
        : (['twin', 'entities', entityType, filters, page, pageSize, ...tail] as const);
    },
  },
  /**
   * Attribute-transition history + per-attribute value series (#1913 C).
   * Keyed under the ``entities`` namespace so a twin edit's blanket
   * ``twinKeys.all`` invalidation (see :func:`useEntityEdit`) refreshes
   * the Verlauf panel too. ``attribute`` is part of the key so the
   * whole-entity log and a single-attribute series cache independently.
   */
  history: (entityType: string, entityId: string, attribute?: string) =>
    ['twin', 'entities', entityType, entityId, 'history', attribute ?? null] as const,
  series: (entityType: string, entityId: string, attribute: string) =>
    ['twin', 'entities', entityType, entityId, 'series', 'attribute', attribute] as const,
  /**
   * Distinct-value facet for one attribute of an entity type (#1934 year
   * axis). Keyed under the ``entities`` namespace so a twin edit's blanket
   * ``twinKeys.all`` invalidation refreshes the year axis too; ``attribute``
   * is part of the key so a second faceted attribute caches independently.
   */
  facet: (entityType: string, attribute: string) =>
    ['twin', 'entities', entityType, 'facet', attribute] as const,
  stats: ['twin', 'stats'] as const,
  lenses: ['twin', 'lenses'] as const,
  lenseCounts: (q?: string | null) => {
    const searchKey = q && q.trim().length > 0 ? q.trim() : undefined;
    // ``searchKey`` only appended when set so the cache-less search-less
    // call (the cold-load + bootstrap path) keeps the legacy 2-tuple key.
    return searchKey === undefined
      ? (['twin', 'lenseCounts'] as const)
      : (['twin', 'lenseCounts', searchKey] as const);
  },
  bootstrap: ['twin', 'bootstrap'] as const,
};

export interface UseEntitiesOptions {
  /**
   * Opt in to server-side pagination. When ``true``, the hook owns
   * ``page`` state, requests ``page`` + ``limit`` from the backend, and
   * exposes ``setPage`` / ``hasMore`` / ``hasPreviousPage`` so the
   * caller can render a Paginator. Defaults to ``false`` so legacy
   * callers (e.g. the agreement supplier/buyer org lookup, which needs
   * the full pool to build a join map) keep getting the unpaginated
   * full list.
   */
  paginated?: boolean;
  /**
   * Page size for paginated reads. Ignored when ``paginated: false``.
   * Defaults to :data:`DEFAULT_ENTITY_PAGE_SIZE` (14) to match
   * ``useDocuments``' ``DEFAULT_PAGE_SIZE``.
   */
  pageSize?: number;
  /**
   * Issue #669 — keys whose JSONB value must be missing or empty
   * string on each row. Forwarded to the backend's ``_missing`` query
   * param; composes with ``filters`` via AND. Used by the FarmPage
   * sub-lenses ``Ohne Rolle`` (``person_role``) and ``Ohne Typ``
   * (``org_type``). Drives a separate React-Query cache slot so
   * switching between role-typed children and the unassigned bucket
   * doesn't trigger a re-fetch on the previous slot.
   */
  filterMissing?: string[];
  /**
   * Issue #637 — case-insensitive substring search applied to
   * ``entity_id`` + JSONB ``attributes::text``. Drives a distinct
   * React-Query cache slot via ``searchKey`` so typing in the global
   * header search doesn't poison the unfiltered cold-load cache, and
   * empty / whitespace strings fall back to the unfiltered key.
   */
  q?: string | null;
}

/**
 * Intersection — not interface — so the discriminated-union shape of
 * ``UseQueryResult`` (loading vs error vs success) survives the spread
 * inside :func:`useEntities`. An ``interface … extends UseQueryResult``
 * would force the value to satisfy every union variant simultaneously,
 * which is unsatisfiable; intersection layers the extra pagination
 * fields on top of whatever discriminated state ``useQuery`` is in.
 */
export type UseEntitiesResult = UseQueryResult<EntityListResponse, Error> & {
  /** Current page (1-indexed; backend's ``Query(1, ge=1)`` default). */
  page: number;
  /** Page size in effect for the current call. */
  pageSize: number;
  /** Convenience accessor — ``data?.entities ?? []``. */
  entities: Record<string, unknown>[];
  /** Convenience accessor — ``data?.total ?? 0`` (filtered total from UDB). */
  total: number;
  /** Convenience accessor — ``data?.primary_key ?? null``. */
  primaryKey: string | null;
  /** ``true`` when more pages exist after the current one. */
  hasMore: boolean;
  /** ``true`` when the user can move backwards (i.e. ``page > 1``). */
  hasPreviousPage: boolean;
  /**
   * Move to a specific 1-indexed page. No-op when ``paginated: false``;
   * the caller's Paginator should still wire to it without a guard.
   */
  setPage: (page: number) => void;
};

export function useEntities(
  entityType?: string,
  filters?: Record<string, string>,
  opts?: UseEntitiesOptions,
): UseEntitiesResult {
  const paginated = opts?.paginated ?? false;
  const pageSize = opts?.pageSize ?? DEFAULT_ENTITY_PAGE_SIZE;
  const [page, setPageState] = useState(1);

  // Page-reset on entityType / filter change — mirrors
  // ``useDocuments`` (`useDocuments.ts:550-585`) so a sub-lense switch
  // or filter edit doesn't strand the user on page 7 of the previous
  // result set. Stringify the filter dict so referential-inequality
  // ("new object literal each render") doesn't trigger a reset.
  //
  // Codex review on PR #638 (P2, 2026-05-02): reset must run *during*
  // render, not in a post-commit ``useEffect``. Pre-fix the effect-
  // based reset let the first render after a filter change build a
  // query key with the stale ``page`` (e.g. page 4) — useQuery fired
  // ``listEntities(newFilters, {page: 4})`` server-side BEFORE the
  // effect re-rendered with ``page = 1`` and re-fired
  // ``listEntities(newFilters, {page: 1})``. Two server round trips
  // and a flash of the wrong page's loading state for every sub-
  // lense click. The during-render setState pattern below lets React
  // bail out and re-render before useQuery's commit phase, so the
  // wasted page-N fetch never reaches the wire (verified by the
  // ``does-not-fire-stale-page-fetch-on-filter-change`` test).
  const filterMissing = opts?.filterMissing;
  const filterMissingKey =
    filterMissing && filterMissing.length > 0 ? filterMissing.join(',') : '';
  // Issue #637: ``q`` participates in the reset key so that typing /
  // clearing the global header search snaps the paginator back to
  // page 1 — same contract as a sub-lense filter switch.
  const searchQuery = opts?.q && opts.q.trim().length > 0 ? opts.q.trim() : '';
  const filtersKey = filters ? JSON.stringify(filters) : '';
  const currentResetKey = `${entityType ?? ''}|${filtersKey}|${filterMissingKey}|${searchQuery}|${pageSize}`;
  const [lastResetKey, setLastResetKey] = useState<string | null>(null);
  let effectivePage = page;
  if (paginated && lastResetKey !== null && lastResetKey !== currentResetKey) {
    // React-recommended pattern for prop-derived state reset
    // (https://react.dev/reference/react/useState#storing-information-from-previous-renders):
    // setState during render triggers an immediate re-render BEFORE
    // useQuery's queryFn commits, so the stale-page request is
    // never issued.
    effectivePage = 1;
    if (page !== 1) setPageState(1);
    setLastResetKey(currentResetKey);
  } else if (lastResetKey === null) {
    // First render — record the baseline key without resetting.
    setLastResetKey(currentResetKey);
  }

  const queryKey = paginated
    ? twinKeys.entities.list(entityType ?? '', filters, effectivePage, pageSize, filterMissing, searchQuery || undefined)
    : twinKeys.entities.list(entityType ?? '', filters, undefined, undefined, filterMissing, searchQuery || undefined);

  const query = useQuery<EntityListResponse>({
    queryKey,
    queryFn: () => {
      const listOptions: { filterMissing?: string[]; q?: string } = {};
      if (filterMissing && filterMissing.length > 0) listOptions.filterMissing = filterMissing;
      if (searchQuery) listOptions.q = searchQuery;
      return twinService.listEntities(
        entityType!,
        filters,
        paginated ? { page: effectivePage, limit: pageSize } : undefined,
        Object.keys(listOptions).length > 0 ? listOptions : undefined,
      );
    },
    enabled: !!entityType,
    // Phase 07-6 kill-list #3 (2026-05-01): without `staleTime`, every
    // FarmPage lense switch re-incurs the full 15-30s entity-list
    // fetch even though entities change at most every few minutes
    // through pipeline rebuilds. 60s matches the natural pipeline
    // tick and aligns with `useLenseCounts` (30s) — long enough to
    // cut user-visible churn, short enough that an edit landing via
    // SSE invalidation still surfaces within ~1 minute.
    staleTime: 60_000,
  });

  // The intersection-cast preserves UseQueryResult's discriminated
  // union (so callers narrowing on ``isSuccess`` etc. still work) while
  // exposing the pagination convenience fields. Without the cast TS
  // collapses the union into a single object literal type and
  // destructures like ``const { data } = useEntities(...)`` lose the
  // proper ``EntityListResponse | undefined`` typing on ``data``.
  return {
    ...query,
    page: paginated ? effectivePage : 1,
    pageSize,
    entities: query.data?.entities ?? [],
    total: query.data?.total ?? 0,
    primaryKey: query.data?.primary_key ?? null,
    hasMore: query.data?.has_more ?? false,
    hasPreviousPage: paginated && effectivePage > 1,
    setPage: paginated ? setPageState : () => undefined,
  } as UseEntitiesResult;
}

// --- Events ---

export function useTwinEvents(filters?: EventFilters) {
  return useQuery<EventListResponse>({
    queryKey: twinKeys.events.list(filters),
    queryFn: () => twinService.listEvents(filters),
  });
}

export function useEventActions() {
  const queryClient = useQueryClient();

  const invalidateEvents = () => {
    queryClient.invalidateQueries({ queryKey: twinKeys.events.all });
    queryClient.invalidateQueries({ queryKey: twinKeys.pruefstatus.all });
  };

  const acknowledge = useMutation({
    mutationFn: (eventId: string) => twinService.acknowledgeEvent(eventId),
    onSuccess: invalidateEvents,
  });

  const dismiss = useMutation({
    mutationFn: (eventId: string) => twinService.dismissEvent(eventId),
    onSuccess: invalidateEvents,
  });

  const resolve = useMutation({
    mutationFn: (eventId: string) => twinService.resolveEvent(eventId),
    onSuccess: invalidateEvents,
  });

  return { acknowledge, dismiss, resolve };
}

// --- Prüfstatus ---

export function usePruefstatus() {
  return useQuery<Pruefstatus>({
    queryKey: twinKeys.pruefstatus.all,
    queryFn: () => twinService.getPruefstatus(),
  });
}

export function usePruefstatusArea(areaId: string) {
  return useQuery({
    queryKey: twinKeys.pruefstatus.area(areaId),
    queryFn: () => twinService.getPruefstatusArea(areaId),
    enabled: !!areaId,
  });
}

// --- Stats ---

export function useTwinStats() {
  return useQuery<TwinStats>({
    queryKey: twinKeys.stats,
    queryFn: () => twinService.getStats(),
  });
}

export function useFarmLenses(enabled: boolean = true) {
  return useQuery<LenseConfig[]>({
    queryKey: twinKeys.lenses,
    queryFn: () => twinService.getLenses(),
    staleTime: 0, // Always refetch — ontology changes need immediate visibility
    enabled,
  });
}

/**
 * Phase 07-6 kill-list #1 (2026-05-01) + perf hotfix (post-rollout):
 * single-shot FarmPage cold-load fetch that collapses lenses + counts
 * + entities into one round trip.
 *
 * Critical: the query key is STABLE per tenant (does NOT include the
 * lense slug). Without this, every lense switch refired a fresh
 * bootstrap RTT and re-paginated UDB for the new entity_type from
 * scratch — observed in prod 2026-05-01 as 40+ second lense switches
 * against tenant ``7552aac2``. The hook's job is the *cold load*;
 * lense switches read entities from the per-type ``useEntities``
 * cache, which has a 60s ``staleTime`` and is pre-seeded by the
 * bootstrap's first response.
 *
 * The ``lense`` argument is passed to the queryFn ONLY on first
 * fetch (the closure captures it); it controls *which* entity list
 * the backend bundles into the response (so cold load = 1 RTT
 * including the active lense's entities). Subsequent lense switches
 * do NOT change the query key; they observe the per-type cache via
 * ``useEntities(newType)``.
 */
export function useFarmBootstrap(
  lense?: string,
  enabled: boolean = true,
  pageSize: number = DEFAULT_ENTITY_PAGE_SIZE,
) {
  const queryClient = useQueryClient();
  return useQuery<{
    lenses: LenseConfig[];
    counts: LenseCounts;
    entities: EntityListResponse | null;
    entity_type: string | null;
    lense: string | null;
  }>({
    queryKey: twinKeys.bootstrap,
    queryFn: async () => {
      // Bootstrap fetches page 1 at the same pageSize the FarmPage's
      // ``useEntities(... { paginated: true })`` will consume, so the
      // seeded cache key matches what the consumer reads. Pre-fix the
      // bootstrap pre-seeded the unpaginated 3-element key
      // (``twin/entities/<type>/undefined``) while the FarmPage call
      // looked up the 5-element paginated key
      // (``twin/entities/<type>/undefined/1/14``); the keys never
      // matched, so ``useEntities`` always re-fetched the same list as
      // a second roundtrip and that re-fetch's onSuccess invalidation
      // re-fired ~14 ``events:expanded`` calls because the entity list
      // was treated as having "changed" relative to its empty cache.
      const data = await twinService.getFarmBootstrap(lense, { page: 1, limit: pageSize });
      if (data.entity_type && data.entities) {
        queryClient.setQueryData(
          twinKeys.entities.list(data.entity_type, undefined, 1, pageSize),
          data.entities,
        );
      }
      return data;
    },
    staleTime: 60_000,
    enabled,
  });
}

export function useLenseCounts(enabled: boolean = true, q?: string | null) {
  // Issue #637: when ``q`` is set the sidebar tallies need to match the
  // search-filtered entity feed. Trim/normalize to ``undefined`` so the
  // search-less call keeps hitting the same legacy 2-tuple cache key
  // (and the same Redis cache slot on the backend).
  const normalized = q && q.trim().length > 0 ? q.trim() : undefined;
  return useQuery<LenseCounts>({
    queryKey: twinKeys.lenseCounts(normalized),
    queryFn: () => twinService.getLenseCounts(normalized),
    staleTime: 30_000,
    enabled,
  });
}

// --- Entity Documents (source documents linked via facts) ---

/**
 * Per-request cap on the backend at
 * ``backend/src/api/v1/endpoints/twin/entities.py:_BULK_ENTITY_IDS_LIMIT``.
 * The hook below chunks the caller's id list into requests of this size
 * and unions the responses, so callers can pass an arbitrary number of
 * ids without seeing the cliff at ENTITY_DOCUMENTS_BULK_CHUNK + 1.
 */
const ENTITY_DOCUMENTS_BULK_CHUNK = 100;

function chunkIds(ids: string[], size: number): string[][] {
  if (ids.length === 0) return [];
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size));
  }
  return chunks;
}

/**
 * Bulk fetch of linked source documents for many entities of one type.
 *
 * Replaces the prior per-entity ``useEntityDocuments`` call site (one
 * React Query per visible entity tile) with a single hook that returns
 * a unified ``{entity_id -> EntitySourceDoc[]}`` map. When the id list
 * exceeds the backend's per-request cap, the work is split into N
 * parallel chunks of ``ENTITY_DOCUMENTS_BULK_CHUNK`` ids and the
 * responses are merged. The query key uses the sorted id list so
 * reordering the input array does not thrash the cache.
 */
export function useEntityDocumentsBulk(entityType?: string, entityIds?: string[]) {
  const ids = (entityIds ?? []).filter((id) => id !== '');
  const sortedIds = [...ids].sort();
  return useQuery<Record<string, import('../services/twinService').EntitySourceDoc[]>>({
    queryKey: ['twin', 'entities', entityType, 'documents-bulk', sortedIds],
    queryFn: async () => {
      const chunks = chunkIds(ids, ENTITY_DOCUMENTS_BULK_CHUNK);
      const results = await Promise.all(
        chunks.map((chunk) => twinService.getEntityDocumentsBulk(entityType!, chunk)),
      );
      const merged: Record<string, import('../services/twinService').EntitySourceDoc[]> = {};
      for (const r of results) Object.assign(merged, r);
      return merged;
    },
    enabled: !!entityType && ids.length > 0,
    // Phase 07-6 kill-list #3 (2026-05-01): same staleness window as
    // `useEntities`. Bulk-doc results are stable per entity_id list
    // and the FarmPage already keys on the sorted id array, so a 60s
    // window covers the common "user clicks back to the same lense"
    // flow without serving stale evidence after a pipeline rebuild.
    staleTime: 60_000,
  });
}

// --- Entity Events (reverse-reference lookup) ---
//
// The single-entity ``useEntityEvents`` hook was removed when the
// FarmPage hoisted to the bulk variant — no other call site referenced
// it. The backend route ``GET /v1/twin/entities/{type}/{id}/events``
// (and its service method ``twin_data_service.get_entity_events``)
// stay in place; if a future single-entity caller needs a hook, the
// 5-line wrapper can be re-added against the same query-key shape.

/**
 * Bulk reverse-reference events lookup — collapses the FarmPage's
 * per-row N+1 (one `GET .../events` per visible row, ~70 calls per
 * cold render) into one HTTP call. Mirrors {@link useEntityDocumentsBulk}'s
 * chunking + sorted-key pattern so cache identity is stable when the
 * caller's id list reorders.
 */
export function useEntityEventsBulk(entityType?: string, entityIds?: string[]) {
  const ids = (entityIds ?? []).filter((id) => id !== '');
  const sortedIds = [...ids].sort();
  return useQuery<Record<string, EntityEvent[]>>({
    queryKey: ['twin', 'entities', entityType, 'events-bulk', sortedIds],
    queryFn: async () => {
      const chunks = chunkIds(ids, ENTITY_DOCUMENTS_BULK_CHUNK);
      const results = await Promise.all(
        chunks.map((chunk) => twinService.getEntityEventsBulk(entityType!, chunk)),
      );
      const merged: Record<string, EntityEvent[]> = {};
      for (const r of results) Object.assign(merged, r);
      return merged;
    },
    enabled: !!entityType && ids.length > 0,
    // Same staleness window as useEntityDocumentsBulk — events for an
    // entity rarely change within a single FarmPage session, and
    // pipeline rebuilds invalidate via SSE within ~1 minute.
    staleTime: 60_000,
  });
}

// --- Entity relations + get-by-id (farm twin-explorer object view) ---

/**
 * Verknüpfungen for one entity: ontology-driven forward/reverse
 * relations with display names, counts and samples.
 */
export function useEntityRelations(entityType?: string, entityId?: string) {
  return useQuery<import('../services/twinService').EntityRelations>({
    queryKey: ['twin', 'entities', entityType, entityId, 'relations'],
    queryFn: () => twinService.getEntityRelations(entityType!, entityId!),
    enabled: !!entityType && !!entityId,
    staleTime: 60_000,
  });
}

/**
 * Fetch specific entities by id (deep links landing on `?item=` beyond
 * the fetched list page, and FK batch-resolution for list subtitles).
 */
export function useEntitiesByIds(entityType?: string, entityIds?: string[]) {
  const ids = (entityIds ?? []).filter((id) => id !== '');
  const sortedIds = [...ids].sort();
  return useQuery<import('../services/twinService').EntityListResponse>({
    queryKey: ['twin', 'entities', entityType, 'by-ids', sortedIds],
    queryFn: () => twinService.queryEntities(entityType!, { entity_ids: ids, limit: ids.length }),
    enabled: !!entityType && ids.length > 0,
    staleTime: 60_000,
  });
}

// --- Entity Source Docs (single-entity linked-documents fetch, #630) ---

/**
 * Fetch linked source documents for one entity (the unfolded-tile
 * "Verknüpfte Dokumente" side panel). Mirrors `useEntityDocumentsBulk`
 * but for the single-entity case where the bulk variant would be
 * wasteful (one entity_id ⇒ one row in the response map).
 */
export function useEntitySourceDocs(entityType?: string, entityId?: string) {
  return useQuery<import('../services/twinService').EntitySourceDoc[]>({
    queryKey: ['twin', 'entities', entityType, entityId, 'documents'],
    queryFn: () => twinService.getEntitySourceDocs(entityType!, entityId!),
    enabled: !!entityType && !!entityId,
    staleTime: 60_000,
  });
}

// --- Attribute-transition history + value series (#1913 C) ---

/**
 * Änderungshistorie for one entity — the append-only attribute
 * transitions (newest-first) that populate the Verlauf panel's log view.
 * Disabled until both ids are known; entities predating the recorder
 * return an empty ``transitions`` list (not an error).
 */
export function useEntityHistory(entityType?: string, entityId?: string, attribute?: string) {
  return useQuery<import('../services/twinService').EntityHistoryResponse>({
    queryKey: twinKeys.history(entityType ?? '', entityId ?? '', attribute),
    queryFn: () =>
      twinService.getEntityHistory(entityType!, entityId!, attribute ? { attribute } : undefined),
    enabled: !!entityType && !!entityId,
    staleTime: 60_000,
  });
}

/**
 * The COMPLETE transition series for one entity (all pages), newest-first —
 * the substrate the ``/farm`` object view folds client-side for as-of
 * navigation (#1913 C). Distinct from :func:`useEntityHistory` (page-1 log
 * display): the as-of reconstruction MUST see the whole series to match the
 * server's unbounded ``get_entity_as_of`` full scan, so a >1000-transition
 * entity does not silently truncate and diverge. Keyed under the same
 * ``history`` namespace (``+ 'complete'``) so a twin edit's blanket
 * invalidation refreshes it too.
 */
export function useEntityTransitionsComplete(entityType?: string, entityId?: string) {
  return useQuery<import('../services/twinService').AttributeTransition[]>({
    queryKey: [...twinKeys.history(entityType ?? '', entityId ?? ''), 'complete'] as const,
    queryFn: () => twinService.getAllEntityTransitions(entityType!, entityId!),
    enabled: !!entityType && !!entityId,
    staleTime: 60_000,
  });
}

/**
 * Per-attribute value series for one entity (the value-history toggle).
 * Fetches only when an attribute is selected — the caller passes
 * ``undefined`` to keep the query idle. Response is the PROVISIONAL
 * ``metric_query``-aligned envelope; a numeric attribute drives the
 * chart, a categorical one the dated value list.
 */
export function useEntityAttributeSeries(entityType?: string, entityId?: string, attribute?: string) {
  return useQuery<import('../services/twinService').EntitySeriesResponse>({
    queryKey: twinKeys.series(entityType ?? '', entityId ?? '', attribute ?? ''),
    queryFn: () => twinService.getEntitySeries(entityType!, entityId!, { attribute: attribute! }),
    enabled: !!entityType && !!entityId && !!attribute,
    staleTime: 60_000,
  });
}

/**
 * Distinct-value facet for one attribute of an entity type (#1934 year
 * axis). Feeds the /farm Erntejahr control: enabled only when the active
 * lens declares a ``year_attribute`` (⇒ ``attribute`` set) and the lens
 * carries an entity_type. Long staleTime — the year axis changes only on
 * ingest/backfill, mirroring ``useLenseCounts`` (30s); an SSE-driven
 * ``twinKeys.all`` invalidation still refreshes it within a tick.
 */
export function useEntityFacet(entityType?: string, attribute?: string) {
  return useQuery<import('../services/twinService').EntityFacetResponse>({
    queryKey: twinKeys.facet(entityType ?? '', attribute ?? ''),
    queryFn: () => twinService.getEntityFacets(entityType!, attribute!),
    enabled: !!entityType && !!attribute,
    staleTime: 30_000,
  });
}

// --- Entity Edit ---

export function useEntityEdit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      entityType,
      entityId,
      fieldUpdates,
    }: {
      entityType: string;
      entityId: string;
      fieldUpdates: Record<string, unknown>;
    }) => twinService.editEntity(entityType, entityId, fieldUpdates),
    onSuccess: () => {
      // Invalidate all twin data since recalculation may affect multiple entities
      queryClient.invalidateQueries({ queryKey: twinKeys.all });
    },
  });
}

// --- Transaction ↔ twin links (#1624-D) ---
//
// The N:N link between a Liquiditätsplanung transaction and a twin item
// (a ``crop_cycle`` harvest entity, or a Bodenprobe document). Consumes the
// #1623 ``liquiditaet_twin_link`` contract via the user-facing backend
// endpoints. Reads are bidirectional: links FOR a transaction (mode
// ``transaction``) and links FOR a twin item (mode ``twin_item``).

/** Stable read-direction key for a twin item: ``document_id`` or ``"{type}/{id}"``. */
function twinItemRef(query: { target_kind: 'document' | 'entity'; document_id?: string; target_entity_type?: string; target_entity_id?: string }): string {
  return query.target_kind === 'document'
    ? query.document_id ?? ''
    : `${query.target_entity_type ?? ''}/${query.target_entity_id ?? ''}`;
}

export const liquiditaetLinkKeys = {
  all: ['liquiditaet', 'links'] as const,
  forTransaction: (transactionId: string) =>
    ['liquiditaet', 'links', 'transaction', transactionId] as const,
  forTwinItem: (targetKind: string, ref: string) =>
    ['liquiditaet', 'links', 'twin_item', targetKind, ref] as const,
};

/**
 * Discriminated query shape for {@link useLiquiditaetLinks}. Mirrors the
 * backend ``GET /twin/liquiditaet/links`` query params (live links only).
 * Pass ``null`` to keep the query disabled (e.g. before a transaction id is
 * known).
 */
export type LiquiditaetLinkQuery =
  | { mode: 'transaction'; transaction_id: string; limit?: number }
  | {
      mode: 'twin_item';
      target_kind: 'document' | 'entity';
      document_id?: string;
      target_entity_type?: string;
      target_entity_id?: string;
      limit?: number;
    };

/** Read the live links FOR a transaction or FOR a twin item. */
export function useLiquiditaetLinks(query: LiquiditaetLinkQuery | null) {
  const queryKey =
    query == null
      ? liquiditaetLinkKeys.all
      : query.mode === 'transaction'
        ? liquiditaetLinkKeys.forTransaction(query.transaction_id)
        : liquiditaetLinkKeys.forTwinItem(query.target_kind, twinItemRef(query));
  return useQuery<LiquiditaetLinkListResponse>({
    queryKey,
    queryFn: () => twinService.listLiquiditaetLinks(query!),
    enabled: query != null,
    staleTime: 30_000,
  });
}

/**
 * Link / unlink a transaction ↔ twin item. On success the whole link
 * namespace is invalidated: a link surfaces under BOTH the transaction read
 * and the twin-item read, so either direction may now be stale.
 */
export function useManageLiquiditaetLink() {
  const queryClient = useQueryClient();
  return useMutation<
    LiquiditaetLinkResult,
    Error,
    Parameters<typeof twinService.manageLiquiditaetLink>[0]
  >({
    mutationFn: (body) => twinService.manageLiquiditaetLink(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: liquiditaetLinkKeys.all });
    },
  });
}
